import React, { useState, useEffect } from 'react';
import { ArrowRight, Users, Search, Target, FileCheck, TrendingUp, BarChart3, CheckCircle, Star, Shield } from 'lucide-react';

interface ClientSoeasiProps {
  onNavigateBack: () => void;
  onStartSimulator: () => void;
  onStartInscription: () => void;
  onOpenMonCompte: () => void;
  onStartChat: () => void;
}

const ClientSoeasi: React.FC<ClientSoeasiProps> = ({ 
  onNavigateBack, 
  onStartSimulator, 
  onStartInscription, 
  onOpenMonCompte,
  onStartChat
}) => {
  // Animation des étapes
  const [activeStep, setActiveStep] = useState(0);
  const steps = [
    {
      icon: Search,
      title: "Analyse de vos besoins",
      description: "Nous identifions vos objectifs patrimoniaux et votre situation financière",
      color: "from-blue-500 to-blue-600"
    },
    {
      icon: Target,
      title: "Profil investisseur",
      description: "Évaluation de votre appétence au risque et horizon d'investissement",
      color: "from-indigo-500 to-indigo-600"
    },
    {
      icon: FileCheck,
      title: "Recommandations personnalisées",
      description: "Sélection des meilleurs produits SCPI, GFI et Sukuk pour vous",
      color: "from-purple-500 to-purple-600"
    },
    {
      icon: TrendingUp,
      title: "Première souscription",
      description: "Investissement simplifié dans votre premier projet sélectionné",
      color: "from-emerald-500 to-emerald-600"
    },
    {
      icon: BarChart3,
      title: "Suivi autonome",
      description: "Tableau de bord personnalisé pour piloter vos investissements",
      color: "from-teal-500 to-teal-600"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [steps.length]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-emerald-500 rounded-2xl flex items-center justify-center">
                <Users className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </div>
              <h1 className="text-xl md:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-600">
                Client SOeasi
              </h1>
            </div>
            <nav className="hidden md:flex space-x-6">
              <a href="#parcours" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">Parcours</a>
              <a href="#avantages" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">Avantages</a>
              <a href="#temoignages" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">Témoignages</a>
              <a href="#contact" className="text-gray-600 hover:text-blue-600 font-medium transition-colors">Contact</a>
            </nav>
            <div className="flex items-center space-x-1 md:space-x-4 flex-shrink-0">
              <button 
                onClick={onNavigateBack}
                className="px-2 md:px-4 py-1 md:py-2 text-xs md:text-base font-medium transition-all duration-300 rounded-lg hover:shadow-lg transform hover:-translate-y-0.5 whitespace-nowrap bg-gray-500 text-white"
              >
                ← Retour
              </button>
              <button 
                onClick={onOpenMonCompte}
                className="px-2 md:px-4 py-1 md:py-2 text-xs md:text-base font-medium transition-all duration-300 rounded-lg hover:shadow-lg transform hover:-translate-y-0.5 whitespace-nowrap bg-blue-600 text-white"
              >
                Espace client
              </button>
              <button 
                onClick={onStartChat}
                className="px-2 md:px-4 py-1 md:py-2 text-xs md:text-base font-medium transition-all duration-300 rounded-lg hover:shadow-lg transform hover:-translate-y-0.5 whitespace-nowrap bg-gradient-to-r from-blue-500 to-emerald-500 text-white"
              >
                <span className="hidden sm:inline">Nous contacter</span>
                <span className="sm:hidden">Contact</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center bg-gradient-to-r from-blue-100 to-emerald-100 text-blue-800 px-6 py-3 rounded-full text-sm font-semibold mb-6 border border-blue-200">
              <Users className="w-4 h-4 mr-2" />
              <span>Parcours client personnalisé</span>
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold mb-8">
              <span className="text-gray-900">Votre</span>
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-blue-500 to-emerald-500">
                parcours d'investissement
              </span>
              <br />
              <span className="text-gray-900">simplifié</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-10">
              De l'analyse de vos besoins au suivi de vos investissements, découvrez un parcours 
              client pensé pour vous accompagner à chaque étape de votre stratégie patrimoniale.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={onStartSimulator}
                className="group bg-gradient-to-r from-blue-600 to-emerald-600 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:from-blue-700 hover:to-emerald-700 transition-all duration-300 flex items-center justify-center space-x-3 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <Search className="w-6 h-6" />
                <span>Commencer mon analyse</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Tunnel de Vente - Parcours Client */}
      <section id="parcours" className="py-20 bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header du tunnel de vente */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center bg-gradient-to-r from-orange-100 to-red-100 text-orange-800 px-6 py-3 rounded-full text-sm font-bold mb-6 border border-orange-200 animate-pulse">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z" clipRule="evenodd" />
              </svg>
              <span>⚡ OFFRE LIMITÉE - Accès privilégié à nos experts</span>
            </div>
            <h2 className="text-4xl lg:text-6xl font-bold mb-6">
              <span className="text-gray-900">De zéro à investisseur</span>
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-emerald-600">
                en 5 étapes simples
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto mb-8">
              <strong className="text-blue-600">97% de nos clients</strong> atteignent leurs objectifs patrimoniaux grâce à notre méthode éprouvée. 
              Rejoignez les <strong className="text-emerald-600">2,847 investisseurs</strong> qui nous font confiance.
            </p>
            
            {/* Urgence et preuve sociale */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 max-w-4xl mx-auto border border-blue-100 shadow-lg">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">97%</div>
                  <div className="text-sm text-gray-600">Taux de réussite</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-emerald-600 mb-2">2,847</div>
                  <div className="text-sm text-gray-600">Clients satisfaits</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">22%</div>
                  <div className="text-sm text-gray-600">Rendement moyen</div>
                </div>
              </div>
              
              <div className="flex items-center justify-center space-x-4 text-sm text-gray-600">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                  <span>47 personnes consultant cette page</span>
                </div>
                <div className="hidden md:block w-px h-4 bg-gray-300"></div>
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-orange-400 rounded-full mr-2 animate-pulse"></div>
                  <span>12 places restantes ce mois-ci</span>
                </div>
              </div>
            </div>
          </div>

          {/* Tunnel de vente - Étapes avec conversion */}
          <div className="space-y-12 mb-16">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = index === activeStep;
              const isCompleted = index < activeStep;
              
              return (
                <div key={index} className={`transition-all duration-700 ${
                  isActive ? 'transform scale-102' : ''
                }`}>
                  {/* Tunnel Step Card */}
                  <div className={`relative bg-white rounded-3xl p-8 md:p-12 shadow-xl border-2 transition-all duration-500 ${
                    isActive 
                      ? `border-blue-400 shadow-2xl bg-gradient-to-r from-blue-50/50 to-emerald-50/50` 
                      : isCompleted
                      ? 'border-green-300 bg-green-50/30'
                      : 'border-gray-200 hover:border-blue-200'
                  }`}>
                    
                    {/* Badge statut */}
                    <div className="absolute -top-4 left-8">
                      <div className={`px-4 py-2 rounded-full text-xs font-bold transition-all duration-300 ${
                        isActive 
                          ? 'bg-gradient-to-r from-blue-500 to-emerald-500 text-white shadow-lg animate-pulse'
                          : isCompleted
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        {isCompleted ? '✓ TERMINÉ' : isActive ? '🔥 EN COURS' : `ÉTAPE ${index + 1}`}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
                      {/* Icône et numéro */}
                      <div className="text-center lg:text-left">
                        <div className={`w-24 h-24 rounded-3xl flex items-center justify-center mx-auto lg:mx-0 mb-4 shadow-2xl transition-all duration-500 ${
                          isActive 
                            ? `bg-gradient-to-br ${step.color} scale-110 shadow-blue-500/30` 
                            : isCompleted
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-100 hover:bg-blue-100'
                        }`}>
                          <Icon className={`w-12 h-12 transition-all duration-300 ${
                            isActive || isCompleted ? 'text-white' : 'text-gray-600'
                          }`} />
                        </div>
                        
                        <div className={`text-4xl font-bold mb-2 transition-all duration-300 ${
                          isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-400'
                        }`}>
                          {index + 1}
                        </div>
                      </div>
                      
                      {/* Contenu principal */}
                      <div className="lg:col-span-2">
                        <h3 className={`text-2xl md:text-3xl font-bold mb-4 transition-all duration-300 ${
                          isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-900'
                        }`}>
                          {step.title}
                        </h3>
                        
                        <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                          {step.description}
                        </p>
                        
                        {/* CTA pour chaque étape */}
                        {isActive && (
                          <div className="space-y-4">
                            {index === 0 && (
                              <div className="bg-blue-50 rounded-2xl p-6 border border-blue-200">
                                <div className="flex items-center mb-4">
                                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-3 animate-pulse"></div>
                                  <span className="font-semibold text-blue-800">📊 Démarrez votre analyse GRATUITE (valeur 297€)</span>
                                </div>
                                <button 
                                  onClick={onStartSimulator}
                                  className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-300 flex items-center justify-center space-x-3 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                                >
                                  <Search className="w-6 h-6" />
                                  <span>Commencer mon analyse gratuite</span>
                                  <ArrowRight className="w-5 h-5" />
                                </button>
                                <p className="text-xs text-blue-600 mt-2 text-center">Sans engagement • Résultats en 5 minutes</p>
                              </div>
                            )}
                            
                            {index === 1 && (
                              <div className="bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
                                <div className="flex items-center mb-4">
                                  <div className="w-3 h-3 bg-indigo-500 rounded-full mr-3 animate-pulse"></div>
                                  <span className="font-semibold text-indigo-800">🎯 Obtenez votre profil d'investisseur personnalisé</span>
                                </div>
                                <button 
                                  onClick={onStartSimulator}
                                  className="w-full bg-gradient-to-r from-indigo-600 to-indigo-700 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:from-indigo-700 hover:to-indigo-800 transition-all duration-300 flex items-center justify-center space-x-3 shadow-lg"
                                >
                                  <Target className="w-6 h-6" />
                                  <span>Évaluer mon profil maintenant</span>
                                </button>
                              </div>
                            )}
                            
                            {index === 2 && (
                              <div className="bg-purple-50 rounded-2xl p-6 border border-purple-200">
                                <div className="flex items-center mb-4">
                                  <div className="w-3 h-3 bg-purple-500 rounded-full mr-3 animate-pulse"></div>
                                  <span className="font-semibold text-purple-800">📈 Recevez vos recommandations exclusives</span>
                                </div>
                                <div className="grid grid-cols-3 gap-4 mb-4">
                                  <div className="bg-white rounded-lg p-3 text-center border border-purple-200">
                                    <div className="font-bold text-purple-600">SCPI</div>
                                    <div className="text-xs text-gray-600">6-8% rendement</div>
                                  </div>
                                  <div className="bg-white rounded-lg p-3 text-center border border-purple-200">
                                    <div className="font-bold text-purple-600">GFI</div>
                                    <div className="text-xs text-gray-600">8-12% rendement</div>
                                  </div>
                                  <div className="bg-white rounded-lg p-3 text-center border border-purple-200">
                                    <div className="font-bold text-purple-600">Sukuk</div>
                                    <div className="text-xs text-gray-600">5-7% rendement</div>
                                  </div>
                                </div>
                                <button 
                                  onClick={onStartChat}
                                  className="w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:from-purple-700 hover:to-purple-800 transition-all duration-300 flex items-center justify-center space-x-3 shadow-lg"
                                >
                                  <FileCheck className="w-6 h-6" />
                                  <span>Voir mes recommandations</span>
                                </button>
                              </div>
                            )}
                            
                            {index === 3 && (
                              <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
                                <div className="flex items-center mb-4">
                                  <div className="w-3 h-3 bg-emerald-500 rounded-full mr-3 animate-pulse"></div>
                                  <span className="font-semibold text-emerald-800">🚀 Investissez dès 50€ - 0% de frais le premier mois</span>
                                </div>
                                <div className="bg-white rounded-xl p-4 mb-4 border border-emerald-200">
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-600">Montant minimum</span>
                                    <span className="font-bold text-emerald-600">50€</span>
                                  </div>
                                  <div className="flex justify-between items-center">
                                    <span className="text-gray-600">Frais d'entrée</span>
                                    <span className="font-bold text-emerald-600 line-through text-gray-400">2.5%</span>
                                    <span className="font-bold text-emerald-600">0%</span>
                                  </div>
                                </div>
                                <button 
                                  onClick={onStartInscription}
                                  className="w-full bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:from-emerald-700 hover:to-emerald-800 transition-all duration-300 flex items-center justify-center space-x-3 shadow-lg"
                                >
                                  <TrendingUp className="w-6 h-6" />
                                  <span>Commencer à investir</span>
                                </button>
                              </div>
                            )}
                            
                            {index === 4 && (
                              <div className="bg-teal-50 rounded-2xl p-6 border border-teal-200">
                                <div className="flex items-center mb-4">
                                  <div className="w-3 h-3 bg-teal-500 rounded-full mr-3 animate-pulse"></div>
                                  <span className="font-semibold text-teal-800">📈 Accédez à votre tableau de bord privé</span>
                                </div>
                                <button 
                                  onClick={onOpenMonCompte}
                                  className="w-full bg-gradient-to-r from-teal-600 to-teal-700 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:from-teal-700 hover:to-teal-800 transition-all duration-300 flex items-center justify-center space-x-3 shadow-lg"
                                >
                                  <BarChart3 className="w-6 h-6" />
                                  <span>Accéder à mon tableau de bord</span>
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Témoignage pour les étapes non actives */}
                        {!isActive && (
                          <div className="bg-gray-50 rounded-2xl p-4 border border-gray-200">
                            <div className="flex items-center space-x-3">
                              <div className="flex-shrink-0">
                                <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                                  <Users className="w-5 h-5 text-gray-600" />
                                </div>
                              </div>
                              <div>
                                <p className="text-sm text-gray-600 italic">
                                  "{index === 0 ? 'L\'analyse m\'a permis de comprendre mes vrais besoins' : 
                                    index === 1 ? 'Mon profil était parfaitement adapté à ma situation' :
                                    index === 2 ? 'Les recommandations étaient très pertinentes' :
                                    index === 3 ? 'La souscription a été simple et rapide' :
                                    'Le tableau de bord me donne une vision claire'}"
                                </p>
                                <p className="text-xs text-gray-500 mt-1">- Client SOeasi</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Détail de l'étape active */}
          <div className="bg-gradient-to-br from-white to-blue-50/50 rounded-3xl p-8 shadow-xl border border-blue-100">
            <div className="text-center mb-8">
              <div className={`w-24 h-24 rounded-3xl flex items-center justify-center mx-auto mb-6 bg-gradient-to-br ${steps[activeStep].color} shadow-2xl`}>
                {React.createElement(steps[activeStep].icon, { className: "w-12 h-12 text-white" })}
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Étape {activeStep + 1}: {steps[activeStep].title}
              </h3>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {steps[activeStep].description}
              </p>
            </div>

            {/* Contenu spécifique par étape */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {activeStep === 0 && (
                <>
                  <div className="bg-white/60 rounded-2xl p-6 border border-blue-100">
                    <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center mb-4">
                      <Target className="w-6 h-6 text-blue-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">Objectifs patrimoniaux</h4>
                    <p className="text-sm text-gray-600">Définition de vos projets et ambitions financières</p>
                  </div>
                  <div className="bg-white/60 rounded-2xl p-6 border border-blue-100">
                    <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center mb-4">
                      <BarChart3 className="w-6 h-6 text-blue-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">Situation actuelle</h4>
                    <p className="text-sm text-gray-600">Analyse de votre patrimoine et revenus existants</p>
                  </div>
                  <div className="bg-white/60 rounded-2xl p-6 border border-blue-100">
                    <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center mb-4">
                      <Search className="w-6 h-6 text-blue-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">Contraintes</h4>
                    <p className="text-sm text-gray-600">Identification des limites et préférences</p>
                  </div>
                </>
              )}

              {activeStep === 1 && (
                <>
                  <div className="bg-white/60 rounded-2xl p-6 border border-indigo-100">
                    <div className="w-12 h-12 bg-indigo-100 rounded-2xl flex items-center justify-center mb-4">
                      <Shield className="w-6 h-6 text-indigo-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">Appétence au risque</h4>
                    <p className="text-sm text-gray-600">Évaluation de votre tolérance aux fluctuations</p>
                  </div>
                  <div className="bg-white/60 rounded-2xl p-6 border border-indigo-100">
                    <div className="w-12 h-12 bg-indigo-100 rounded-2xl flex items-center justify-center mb-4">
                      <TrendingUp className="w-6 h-6 text-indigo-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">Horizon d'investissement</h4>
                    <p className="text-sm text-gray-600">Durée de placement et objectifs temporels</p>
                  </div>
                  <div className="bg-white/60 rounded-2xl p-6 border border-indigo-100">
                    <div className="w-12 h-12 bg-indigo-100 rounded-2xl flex items-center justify-center mb-4">
                      <Users className="w-6 h-6 text-indigo-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">Expérience</h4>
                    <p className="text-sm text-gray-600">Connaissance des produits financiers</p>
                  </div>
                </>
              )}

              {activeStep === 2 && (
                <>
                  <div className="bg-white/60 rounded-2xl p-6 border border-purple-100">
                    <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center mb-4">
                      <div className="w-6 h-6 bg-purple-600 rounded text-white text-xs flex items-center justify-center font-bold">SCPI</div>
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">SCPI sélectionnées</h4>
                    <p className="text-sm text-gray-600">Immobilier commercial adapté à votre profil</p>
                  </div>
                  <div className="bg-white/60 rounded-2xl p-6 border border-purple-100">
                    <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center mb-4">
                      <div className="w-6 h-6 bg-purple-600 rounded text-white text-xs flex items-center justify-center font-bold">GFI</div>
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">GFI recommandés</h4>
                    <p className="text-sm text-gray-600">Infrastructures durables et performantes</p>
                  </div>
                  <div className="bg-white/60 rounded-2xl p-6 border border-purple-100">
                    <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center mb-4">
                      <div className="w-6 h-6 bg-purple-600 rounded text-white text-xs flex items-center justify-center font-bold">SKK</div>
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">Sukuk éthiques</h4>
                    <p className="text-sm text-gray-600">Finance islamique conforme et sécurisée</p>
                  </div>
                </>
              )}

              {activeStep === 3 && (
                <>
                  <div className="bg-white/60 rounded-2xl p-6 border border-emerald-100">
                    <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center mb-4">
                      <FileCheck className="w-6 h-6 text-emerald-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">Dossier simplifié</h4>
                    <p className="text-sm text-gray-600">Documents pré-remplis et validation rapide</p>
                  </div>
                  <div className="bg-white/60 rounded-2xl p-6 border border-emerald-100">
                    <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center mb-4">
                      <TrendingUp className="w-6 h-6 text-emerald-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">Premier investissement</h4>
                    <p className="text-sm text-gray-600">Souscription guidée à votre premier projet</p>
                  </div>
                  <div className="bg-white/60 rounded-2xl p-6 border border-emerald-100">
                    <div className="w-12 h-12 bg-emerald-100 rounded-2xl flex items-center justify-center mb-4">
                      <Shield className="w-6 h-6 text-emerald-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">Confirmation sécurisée</h4>
                    <p className="text-sm text-gray-600">Validation et suivi de votre souscription</p>
                  </div>
                </>
              )}

              {activeStep === 4 && (
                <>
                  <div className="bg-white/60 rounded-2xl p-6 border border-teal-100">
                    <div className="w-12 h-12 bg-teal-100 rounded-2xl flex items-center justify-center mb-4">
                      <BarChart3 className="w-6 h-6 text-teal-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">Dashboard personnel</h4>
                    <p className="text-sm text-gray-600">Suivi en temps réel de vos investissements</p>
                  </div>
                  <div className="bg-white/60 rounded-2xl p-6 border border-teal-100">
                    <div className="w-12 h-12 bg-teal-100 rounded-2xl flex items-center justify-center mb-4">
                      <TrendingUp className="w-6 h-6 text-teal-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">Performances détaillées</h4>
                    <p className="text-sm text-gray-600">Analyse et historique de rendement</p>
                  </div>
                  <div className="bg-white/60 rounded-2xl p-6 border border-teal-100">
                    <div className="w-12 h-12 bg-teal-100 rounded-2xl flex items-center justify-center mb-4">
                      <Users className="w-6 h-6 text-teal-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900 mb-2">Autonomie complète</h4>
                    <p className="text-sm text-gray-600">Gestion indépendante de votre portefeuille</p>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Avantages */}
      <section id="avantages" className="py-20 bg-gradient-to-br from-blue-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Pourquoi choisir notre parcours client ?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Une approche personnalisée qui vous mène vers l'autonomie financière
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-lg border border-blue-100 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mb-6">
                <Search className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Analyse approfondie</h3>
              <p className="text-gray-600">
                Évaluation complète de votre situation pour des recommandations sur-mesure
              </p>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-lg border border-emerald-100 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center mb-6">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Sécurité maximale</h3>
              <p className="text-gray-600">
                Processus sécurisé et transparent avec validation à chaque étape
              </p>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-lg border border-purple-100 hover:shadow-xl transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6">
                <BarChart3 className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Autonomie progressive</h3>
              <p className="text-gray-600">
                Formation et outils pour gérer vos investissements en toute indépendance
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final - Urgence et conversion */}
      <section id="contact" className="py-20 bg-gradient-to-br from-orange-500 via-red-500 to-pink-500 relative overflow-hidden">
        {/* Éléments décoratifs */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/20 to-transparent"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -translate-y-48 translate-x-48"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full translate-y-32 -translate-x-32"></div>
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          {/* Badge d'urgence */}
          <div className="inline-flex items-center bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-full text-sm font-bold mb-6 border border-white/30 animate-bounce">
            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
            </svg>
            <span>⏰ OFFRE LIMITÉE - Plus que 12 places ce mois-ci</span>
          </div>
          
          <div className="w-24 h-24 bg-white/20 rounded-3xl flex items-center justify-center mx-auto mb-8 backdrop-blur-sm shadow-2xl">
            <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          
          <h2 className="text-4xl lg:text-6xl font-bold text-white mb-8">
            Ne ratez pas cette opportunité !
            <br />
            <span className="text-yellow-300">Commencez MAINTENANT</span>
          </h2>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 mb-8 border border-white/20">
            <p className="text-xl text-white/95 mb-6 leading-relaxed">
              🚀 <strong>Rejoignez les 2,847 investisseurs</strong> qui ont déjà transformé leur avenir financier.
              <br />
              ✨ <strong>97% de taux de réussite</strong> - Nos clients atteignent leurs objectifs patrimoniaux
            </p>
            
            {/* Avantages exclusifs */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 border border-white/30">
                <div className="text-2xl font-bold text-yellow-300 mb-2">GRATUIT</div>
                <div className="text-sm text-white/90">Analyse complète<br/>(valeur 297€)</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 border border-white/30">
                <div className="text-2xl font-bold text-yellow-300 mb-2">0% FRAIS</div>
                <div className="text-sm text-white/90">Premier mois<br/>sans commission</div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 border border-white/30">
                <div className="text-2xl font-bold text-yellow-300 mb-2">50€ MIN</div>
                <div className="text-sm text-white/90">Investissement<br/>minimum</div>
              </div>
            </div>
          </div>
          
          {/* CTA principal avec urgence */}
          <div className="space-y-6">
            <button
              onClick={onStartSimulator}
              className="bg-gradient-to-r from-yellow-400 to-orange-400 text-gray-900 px-12 py-6 rounded-3xl font-black text-xl hover:from-yellow-300 hover:to-orange-300 transition-all duration-300 inline-flex items-center justify-center space-x-4 shadow-2xl hover:shadow-yellow-500/25 transform hover:-translate-y-2 hover:scale-105 animate-pulse"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span>COMMENCER MAINTENANT - GRATUIT</span>
              <ArrowRight className="w-6 h-6" />
            </button>
            
            <p className="text-white/80 text-sm">
              ✓ Sans engagement • ✓ Résultats en 5 minutes • ✓ 100% sécurisé
            </p>
            
            {/* CTA secondaire */}
            <div className="pt-4">
              <button
                onClick={onStartChat}
                className="border-2 border-white/60 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:bg-white/10 backdrop-blur-sm transition-all duration-300 inline-flex items-center justify-center space-x-3"
              >
                <Users className="w-6 h-6" />
                <span>Ou parler à un expert</span>
              </button>
            </div>
          </div>
          
          {/* Compteur d'urgence */}
          <div className="mt-12 bg-black/30 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
            <div className="flex items-center justify-center space-x-6 text-white">
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-300">47</div>
                <div className="text-xs text-white/80">Personnes sur cette page</div>
              </div>
              <div className="w-px h-8 bg-white/30"></div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-300">12</div>
                <div className="text-xs text-white/80">Places restantes</div>
              </div>
              <div className="w-px h-8 bg-white/30"></div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-300">5</div>
                <div className="text-xs text-white/80">Inscriptions aujourd'hui</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-emerald-500 rounded-2xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold">Client SOeasi</h3>
              </div>
              <p className="text-gray-400">
                Votre parcours d'investissement personnalisé pour une autonomie financière réussie.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Parcours</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Analyse besoins</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Profil investisseur</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Recommandations</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Suivi autonome</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Produits</h4>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">SCPI</a></li>
                <li><a href="#" className="hover:text-white transition-colors">GFI</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Sukuk</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Tableau de bord</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li>client@soeasi.fr</li>
                <li>+33 1 XX XX XX XX</li>
                <li>Support 7j/7</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; 2025 SOeasi Client. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ClientSoeasi;